/**
 * admin/shared.tsx — presentational pieces shared by the three queue pages (review, moderation,
 * support). Mirrors community/shared.tsx's approach exactly: one inline <style> block, class names
 * local to these pages, reusing the frame's global --tb-* tokens (App.tsx :root). Provisional
 * engineering UI — there is no signed-off design for these screens yet, so a visible banner says so.
 */
export { SignedOut } from '../tools/shared';
export { shortWhen } from '../community/shared';

/** The always-present provisional-status banner for the admin/moderator queues. */
export function ProvisionalBanner() {
  return (
    <p className="tb-provisional" role="note">
      <strong>Provisional interface.</strong> This screen is a working engineering build for an
      appointed reviewer/moderator. Its layout and wording are not the final design.
    </p>
  );
}

/** A plain, non-crashing message for a caller who reached a queue without the role it needs. */
export function NotPermitted({ what }: { what: string }) {
  return (
    <section aria-labelledby="not-permitted-heading">
      <h1 id="not-permitted-heading">{what}</h1>
      <p className="tb-err" role="alert">
        You don&rsquo;t currently hold the appointment this screen needs. If you believe you should,
        ask the platform owner to check the administration surface.
      </p>
    </section>
  );
}

export const ADMIN_STYLES = `
  .tb-admin { max-width: 64rem; }
  .tb-admin h1 { margin-top: 0; }
  .tb-admin .tb-muted { color: var(--tb-muted); }

  .tb-admin .tb-provisional {
    border: 1px solid var(--tb-line);
    border-left: 4px solid var(--tb-banner-line);
    background: var(--tb-banner-bg);
    color: var(--tb-banner-ink);
    padding: 0.6rem 0.9rem;
    margin: 0 0 1.25rem;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .tb-admin ul.tb-list { list-style: none; margin: 1rem 0; padding: 0; }
  .tb-admin ul.tb-list > li {
    border: 1px solid var(--tb-line);
    border-radius: 6px;
    padding: 0.75rem 1rem;
    margin: 0.6rem 0;
    background: var(--tb-surface);
  }
  .tb-admin .tb-item-title { font-weight: 600; font-size: 1rem; }
  .tb-admin .tb-item-meta { color: var(--tb-muted); font-size: 0.85rem; margin: 0.15rem 0 0.5rem; }
  .tb-admin .tb-item-body { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0.4rem 0; }

  .tb-admin dl.tb-fields { margin: 0.5rem 0; }
  .tb-admin dl.tb-fields dt { font-weight: 600; font-size: 0.85rem; margin-top: 0.5rem; }
  .tb-admin dl.tb-fields dd { margin: 0.1rem 0 0; white-space: pre-wrap; overflow-wrap: anywhere; }

  .tb-admin fieldset {
    border: 1px solid var(--tb-line); border-radius: 6px; margin: 0.75rem 0 0; padding: 0.6rem 0.75rem;
  }
  .tb-admin legend { font-weight: 600; padding: 0 0.4rem; font-size: 0.85rem; }
  .tb-admin label.tb-field { display: block; margin: 0.5rem 0; font-size: 0.9rem; }
  .tb-admin label.tb-field > span { display: block; font-weight: 600; margin-bottom: 0.2rem; }
  .tb-admin label.tb-check { display: flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; margin: 0.35rem 0; }
  .tb-admin input[type="text"], .tb-admin textarea, .tb-admin select {
    width: 100%; font: inherit;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-bg); color: var(--tb-ink);
  }
  .tb-admin textarea { min-height: 3.5rem; resize: vertical; }

  .tb-admin .tb-actions { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.6rem; }
  .tb-admin button.tb-submit {
    font: inherit; padding: 0.4rem 0.9rem;
    border: 1px solid var(--tb-accent); border-radius: 6px;
    background: var(--tb-accent); color: var(--tb-accent-ink); cursor: pointer;
  }
  .tb-admin button.tb-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .tb-admin button.tb-danger {
    font: inherit; padding: 0.4rem 0.9rem;
    border: 1px solid #8a2f2f; border-radius: 6px;
    background: #fff; color: #8a2f2f; cursor: pointer;
  }
  .tb-admin button.tb-danger:disabled { opacity: 0.5; cursor: not-allowed; }

  .tb-admin .tb-err { color: var(--tb-banner-ink); background: var(--tb-banner-bg);
    border: 1px solid var(--tb-banner-line); border-radius: 4px; padding: 0.4rem 0.6rem;
    margin: 0.5rem 0; font-size: 0.85rem; }
  .tb-admin .tb-empty { color: var(--tb-muted); font-style: italic; margin: 1rem 0; }
`;
